import { styled } from 'dripsy';
import React, { Fragment } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useModal } from 'react-native-modalfy';
import { observer } from 'mobx-react-lite';
import { useNavigation } from '@react-navigation/native';

import {
  ApplePaySvg,
  BaseText,
  Button,
  ButtonStyleEnum,
  GooglePaySvg,
  Icon,
  Line,
  NavigateButton,
  Separator,
  SettingsLayout,
  Touchable,
} from '@components';
import { useNavOptionsSet, usePaymentMethod } from '@hooks';
import {
  ProfileNavigatorNavigationPropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { IS_WEB, parseApiError } from '@utils';
import { modals, ModalStackParamsType } from '@modals';
import {
  PaymentMethodEnum,
  SUBSCRIPTION_ID_BY_TARIFF_CODE,
  TariffPlanCodeEnum,
  TariffPlanStatusEnum,
} from '@constants';
import {
  AnalyticsService,
  IAPService,
  ToastService,
} from '@services';
import { ColorMapType } from '@types';
import { useAuthModel } from '@models/AuthModel';

const PlanCardContainer = styled(View)({
  width: ['100%', 418],
  borderRadius: 6,
  bg: '$background10',
  px: '$8',
});

const PlanTitleContainer = styled(View)({
  width: '100%',
  alignItems: 'center',
});

const PlanTitle = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: 'text.xlarge',
});

const PropRow = styled(View)({
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PropTitle = styled(BaseText)({
  flex: 1,
  color: '$heading800',
  fontWeight: 'semi',
  variant: 'text.medium',
});

const PropText = styled(BaseText)({
  flex: 1,
  color: '$heading800',
  variant: 'text.medium',
});

const PropTextContainer = styled(View)({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
});

const PriceText = styled(BaseText)({
  color: '$text300',
  variant: 'text.medium',
});

const statusTextColorMap: ColorMapType<TariffPlanStatusEnum> = {
  [TariffPlanStatusEnum.Active]: '$primary300',
  [TariffPlanStatusEnum.Inactive]: '$error300',
  [TariffPlanStatusEnum.Canceled]: '$waring300',
};

const statusBackgroundColorMap: ColorMapType<TariffPlanStatusEnum> = {
  [TariffPlanStatusEnum.Active]: '$primary80',
  [TariffPlanStatusEnum.Inactive]: '$error80',
  [TariffPlanStatusEnum.Canceled]: '$waring80',
};

const statusLocalKeyMap: Record<TariffPlanStatusEnum, string> = {
  [TariffPlanStatusEnum.Active]: 'active',
  [TariffPlanStatusEnum.Inactive]: 'inactive',
  [TariffPlanStatusEnum.Canceled]: 'canceled',
};

interface IStatusTextStyleProps {
  status: TariffPlanStatusEnum;
}
const StatusText = styled(BaseText)(
  (props: IStatusTextStyleProps) => ({
    color: statusTextColorMap[props.status],
    variant: 'text.medium',
    bg: statusBackgroundColorMap[props.status],
    px: '$4',
    py: '$2',
    borderRadius: 4,
    fontWeight: 'semi',
  }),
);

const CancelButton = styled(Button)({
  bg: '$background10',
});

const Text = styled(BaseText)({
  color: '$error300',
  variant: 'text.medium',
  p: '$5',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  py: '$6',
});

const ManageSubscriptionText = styled(BaseText)({
  color: '$background50',
  variant: 'text.small',
  textAlign: 'center',
});

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Subscription,
];

function renderPaymentMethod(method: PaymentMethodEnum) {
  switch (method) {
    case PaymentMethodEnum.ApplePay:
      return (
        <ApplePaySvg
          contentColor="$heading900"
          viewBox="0 0 55 22"
          height={18}
          width={41}
        />
      );
    case PaymentMethodEnum.GooglePay:
      return (
        <GooglePaySvg
          contentColor="$heading900"
          viewBox="0 0 62 25"
          height={18}
          width={47}
        />
      );
    default:
      return <Icon name="card-light" color="$heading800" size={20} />;
  }
}

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'CreditCards'>;

export const SubscriptionScreen = observer(() => {
  const { t } = useTranslation();
  const viewer = useViewerModel();
  const nav = useNavigation<NavigationPropType>();
  const modal = useModal<ModalStackParamsType>();
  const paymentMethod = usePaymentMethod();
  const authModel = useAuthModel();

  useNavOptionsSet({ title: t('subscription.title') });

  async function cancelSubscription() {
    const tariffPlan = viewer.currentUser?.tariffPlan;
    const tariffPlanId = tariffPlan!.id;

    try {
      await tariffPlan?.cancelSubscription.run();

      ToastService.showSuccess(
        t('subscription.successCancelMessage'),
      );

      AnalyticsService.trackSubscriptionCancel(tariffPlanId);
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function changeTariffPlan(
    tariffPlan: TariffPlanCodeEnum,
    couponId?: number,
  ) {
    try {
      const changeTariffPlanRes =
        await viewer.currentUser?.tariffPlan.changeTariffPlan.run({
          tariffPlan,
          couponId,
        });

      if (changeTariffPlanRes?.requiredAction) {
        const paymentIntent = await paymentMethod.handleCardAction(
          changeTariffPlanRes,
        );

        if (!paymentIntent) {
          throw new Error();
        }

        await viewer.currentUser?.tariffPlan.confirmChangeTariffPlan.run(
          {
            paymentIntentId: paymentIntent.id,
            tariffPlan,
          },
        );
      }

      ToastService.showSuccess(
        t('subscription.successChangeMessage'),
      );
    } catch (error) {
      ToastService.showError(parseApiError(error).message);

      throw error;
    }
  }

  function openCancelSubscription() {
    switch (viewer.currentUser?.tariffPlan.method) {
      case PaymentMethodEnum.ApplePay:
        return IAPService.openAppleAppStore();
      case PaymentMethodEnum.GooglePay:
        return IAPService.openGooglePlayStore(
          SUBSCRIPTION_ID_BY_TARIFF_CODE[
            viewer.currentUser!.tariffPlan.id
          ],
        );
      default:
        return modal.openModal(modals.ConfirmationModal, {
          title: t('subscription.cancelSubscription'),
          subtitle: t('subscription.cancelSubscriptionSubtitle'),
          buttons: [
            { text: t('subscription.yes') },
            { text: t('subscription.no') },
          ],
          onConfirm: async () => {
            modal.openModal(modals.LoaderModal);
            await cancelSubscription();
            modal.closeAllModals();
          },
        });
    }
  }

  function onSelect(item: TariffPlanCodeEnum, couponId?: number) {
    modal.openModal(modals.ConfirmationModal, {
      title: t('subscription.planChanging'),
      subtitle: t('subscription.planChangingSubtitle'),
      buttons: [
        {
          text: t('subscription.change'),
          style: ButtonStyleEnum.Flat,
        },
        { text: t('subscription.cancel') },
      ],
      onConfirm: async () => {
        try {
          modal.openModal(modals.LoaderModal);
          await changeTariffPlan(item, couponId);
          modal.closeAllModals();
        } catch (error) {
          modal.closeModals(modals.LoaderModal);
          modal.closeModals(modals.ConfirmationModal);
        }
      },
    });
  }

  function navigateToChangePlan() {
    if (IS_WEB) {
      modal.openModal(modals.ChangeSubscriptionPlanModal, {
        onSelect: onSelect,
      });
    } else {
      nav.navigate(screens.ChangeSubscriptionPlan);
    }
  }

  function navigateToCreditCards() {
    nav.navigate(screens.CreditCards);
  }

  function logout() {
    authModel.signOut.run();
    authModel.unauthorize();
  }

  function openLogoutModal() {
    modal.openModal(modals.ConfirmationModal, {
      title: t('profile.logout'),
      subtitle: t('profile.logoutSubtitle'),
      buttons: [
        { text: t('profile.logout') },
        { text: t('profile.cancel') },
      ],
      onConfirm: () => {
        logout();
        modal.closeModal();
      },
    });
  }

  const isCardMethod =
    viewer.currentUser?.tariffPlan.method ===
    PaymentMethodEnum.Stripe;

  const status =
    viewer.currentUser?.tariffPlan.status ??
    TariffPlanStatusEnum.Inactive;

  const endDateTitle =
    status === TariffPlanStatusEnum.Active
      ? t('subscription.renewDate')
      : t('subscription.endDate');

  return (
    <SettingsLayout
      title={t('subscription.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <Separator size={['$5', '$6']} />

      <PlanCardContainer>
        <Separator size="$8" />
        <PlanTitleContainer>
          <PlanTitle>
            {`${t('subscription.mySubscriptionPlan')} 🤟`}
          </PlanTitle>
        </PlanTitleContainer>
        <Separator size="$8" />
        <Line />
        <Separator size="$8" />

        <View>
          <PropRow>
            <PropTitle>{t('subscription.plan')}</PropTitle>
            <PropText>
              {t(
                `common.subscriptionPlans.${viewer.currentUser?.tariffPlan.id}.title`,
              )}{' '}
              <PriceText>
                ({viewer.currentUser?.tariffPlan.formattedPrice})
              </PriceText>
            </PropText>
          </PropRow>

          <Separator />

          <PropRow>
            <PropTitle>{t('subscription.startDate')}</PropTitle>
            <PropText>
              {viewer.currentUser?.tariffPlan.formattedStartTime}
            </PropText>
          </PropRow>

          <Separator />

          <PropRow>
            <PropTitle>{endDateTitle}</PropTitle>
            <PropText>
              {viewer.currentUser?.tariffPlan.formattedEndTime}
            </PropText>
          </PropRow>

          <Separator size="$5" />

          <PropRow>
            <PropTitle>
              {t('subscription.subscriptionStatus')}
            </PropTitle>
            <PropTextContainer>
              <StatusText status={status}>
                {t('subscription.status', {
                  context: statusLocalKeyMap[status],
                })}
              </StatusText>
            </PropTextContainer>
          </PropRow>

          <Separator size="$5" />

          <PropRow>
            <PropTitle>{t('subscription.paymentMethod')}</PropTitle>
            <PropTextContainer>
              {renderPaymentMethod(
                viewer.currentUser?.tariffPlan.method as any,
              )}
              {isCardMethod && (
                <PropText>
                  {' '}
                  {t('subscription.creditCardMethod')}
                </PropText>
              )}
            </PropTextContainer>
          </PropRow>
        </View>

        {IS_WEB ? (
          <Fragment>
            <Separator size="$7" />

            <Button onPress={navigateToChangePlan}>
              {t('subscription.changeMyPlan')}
            </Button>
            <Separator size="$5" />

            <CancelButton
              buttonStyle={ButtonStyleEnum.Outline}
              onPress={openCancelSubscription}
              disabled={!viewer.currentUser?.tariffPlan.canBeCanceled}
            >
              {t('subscription.cancelSubscription')}
            </CancelButton>
          </Fragment>
        ) : (
          <Fragment>
            <Separator size="$6" />
            <ManageSubscriptionText>
              {t('subscription.manageSubscription')}
            </ManageSubscriptionText>
          </Fragment>
        )}
        <Separator size="$8" />
      </PlanCardContainer>

      <Separator size="$8" />

      {IS_WEB && (
        <Fragment>
          <Line />
          <NavigateButton
            text={t('subscription.cardDetails')}
            onPress={navigateToCreditCards}
          />
        </Fragment>
      )}

      {
        // TODO: replace this logic in the feature by proving access to profile screen
      }
      {!viewer.currentUser?.tariffPlan.isActive && (
        <Fragment>
          <Line />
          <TouchableContainer onPress={openLogoutModal}>
            <Icon name="logout-light" color="$error300" size={24} />
            <Text>{t('subscription.logout')}</Text>
          </TouchableContainer>
        </Fragment>
      )}
    </SettingsLayout>
  );
});
